import type {
  AccountEntity,
  AccountGroupEntity,
} from '@actual-app/core/types/models';

export type AccountClassification = 'onbudget' | 'offbudget' | 'closed';

export type AccountGroupSection = {
  group: AccountGroupEntity | null;
  accounts: AccountEntity[];
};

export function accountDragType(classification: AccountClassification) {
  return classification === 'closed'
    ? 'account-closed'
    : classification === 'offbudget'
      ? 'account-offbudget'
      : 'account-onbudget';
}

export function getAccountDropTarget(
  bucket: AccountEntity[],
  dropPos: 'top' | 'bottom' | null,
  targetId?: string,
) {
  if (targetId) {
    const targetIndex = bucket.findIndex(account => account.id === targetId);
    if (targetIndex >= 0) {
      return dropPos === 'bottom'
        ? (bucket[targetIndex + 1]?.id ?? null)
        : bucket[targetIndex].id;
    }
  }
  return dropPos === 'top' ? (bucket[0]?.id ?? null) : null;
}

export function getAccountGroupMoveTarget(
  groups: AccountGroupEntity[],
  index: number,
  direction: 'up' | 'down',
) {
  if (direction === 'up') return groups[index - 1]?.id ?? null;
  return groups[index + 2]?.id ?? null;
}

function matchesClassification(
  account: AccountEntity,
  classification: AccountClassification,
) {
  if (classification === 'closed') {
    return account.closed === 1;
  }

  return (
    account.closed === 0 &&
    (classification === 'offbudget'
      ? account.offbudget === 1
      : account.offbudget === 0)
  );
}

export function getAccountGroupSections(
  accounts: AccountEntity[],
  groups: AccountGroupEntity[],
  classification: AccountClassification,
  includeEmptyGroups = false,
): AccountGroupSection[] | null {
  const visibleAccounts = accounts.filter(account =>
    matchesClassification(account, classification),
  );
  const activeGroups = groups
    .filter(group => !group.tombstone)
    .sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
  if (activeGroups.length === 0) {
    return null;
  }
  const groupIds = new Set(activeGroups.map(group => group.id));
  const sections: AccountGroupSection[] = [];

  for (const group of activeGroups) {
    const groupAccounts = visibleAccounts.filter(
      account => account.group === group.id,
    );
    if (groupAccounts.length > 0 || includeEmptyGroups) {
      sections.push({ group, accounts: groupAccounts });
    }
  }

  const ungroupedAccounts = visibleAccounts.filter(
    account => account.group == null || !groupIds.has(account.group),
  );
  if (ungroupedAccounts.length > 0 || includeEmptyGroups) {
    sections.push({ group: null, accounts: ungroupedAccounts });
  }

  return sections;
}
